import { useBillingConfirmCheckout } from "@/lib/api/requests/billing.requests";
import { Button } from "@/ui-components/ui/button";
import { BlueLoadingFallback } from "@/ui-components/ui/suspense-wrapper";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const MAX_CONFIRM_ATTEMPTS = 8;
const RETRY_DELAY_MS = 1500;

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync: confirmCheckout } = useBillingConfirmCheckout();
  const [isProcessing, setIsProcessing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const attemptRef = useRef(0);
  const confirmCheckoutRef = useRef(confirmCheckout);

  const sessionId = searchParams.get("session_id");
  const requestedPlan = searchParams.get("plan");

  useEffect(() => {
    confirmCheckoutRef.current = confirmCheckout;
  }, [confirmCheckout]);

  useEffect(() => {
    let isActive = true;
    let timeoutId: number | undefined;

    if (!sessionId) {
      navigate("/choose-plan", { replace: true });
      return;
    }

    const finalizeCheckout = async () => {
      try {
        const response = await confirmCheckoutRef.current({ sessionId });

        if (!response.confirmed) {
          if (attemptRef.current >= MAX_CONFIRM_ATTEMPTS) {
            throw new Error("Die Buchung konnte nicht rechtzeitig bestaetigt werden.");
          }

          attemptRef.current += 1;
          timeoutId = window.setTimeout(finalizeCheckout, RETRY_DELAY_MS);
          return;
        }

        if (isActive) {
          const resolvedPlan = response.plan || requestedPlan || "basic";
          window.location.replace(`/?success=true&plan=${resolvedPlan}`);
        }
      } catch (error) {
        if (isActive) {
          setIsProcessing(false);
          setErrorMessage(error instanceof Error ? error.message : "Die Buchung konnte nicht bestaetigt werden.");
        }
      }
    };

    void finalizeCheckout();

    return () => {
      isActive = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [navigate, requestedPlan, sessionId]);

  if (isProcessing) {
    return <BlueLoadingFallback text="Abo wird bestaetigt..." />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg ring-1 ring-slate-200">
        <h1 className="text-2xl font-semibold text-slate-900">Checkout bestaetigen fehlgeschlagen</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          {errorMessage || "Die Rueckkehr aus Stripe konnte nicht abgeschlossen werden."}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <Button onClick={() => window.location.reload()} className="w-full">
            Erneut versuchen
          </Button>
          <Button variant="outline" onClick={() => navigate("/choose-plan", { replace: true })} className="w-full">
            Zur Plan-Auswahl
          </Button>
        </div>
      </div>
    </div>
  );
}
